import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { NavigationProvider } from './contexts/NavigationContext';
import Header from './components/Layout/Header';
import Hero from './components/Hero/Hero';
import StakingTierSection from './components/StakingTierSection/StakingTierSection';
import Features from './components/Features/Features';
import SwapInterface from './components/SwapInterface/SwapInterface';
import EcosystemResponsive from './components/Ecosystem/EcosystemResponsive';
import CertikAuditBanner from './components/CertikAuditBanner/CertikAuditBanner';
import Vaults from './components/Vaults/Vaults';
import Roadmap from './components/Roadmap/Roadmap';
import Tokenomics from './components/Tokenomics/Tokenomics';
import Scanner from './components/Scanner/Scanner';
import Footer from './components/Footer/Footer';
import WalletModal from './components/WalletModal/WalletModal';
import DocumentationPage from './pages/DocumentationPage';
import TermsModal from './components/TermsModal/TermsModal';
import TermsOfUse from './pages/TermsOfUse';
import { providerEventHandler, errorLogger, ErrorCategory, ErrorSeverity } from './utils/errorLogger';
import './styles/globals.css';
import './App.css';

const TERMS_VERSION = '2025-09-22';
const TERMS_STORAGE_KEY = 'usdp_terms_accepted_version';
const TERMS_SESSION_KEY = 'usdp_terms_shown_session';

function AppContent() {
  const location = useLocation();

  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState<boolean>(() => {
    try {
      const path = window.location?.pathname || '/';
      const isDocs = path.includes('/docs');
      const isScanner = path.includes('/scanner');
      if (isDocs || isScanner) return false;

      const accepted = localStorage.getItem(TERMS_STORAGE_KEY);
      const shownSession = sessionStorage.getItem(TERMS_SESSION_KEY);
      return accepted !== TERMS_VERSION || !shownSession;
    } catch {
      // If storage not available, default to showing
      return true;
    }
  });

  const isDocsPage = location.pathname.startsWith('/docs');
  const isScannerPage = location.pathname === '/scanner';

  // Initialize error logging and provider event handling
  useEffect(() => {
    // Initialize provider event handler for wallet disconnections and account changes
    providerEventHandler.initialize();
    
    // Add global error handler for unhandled errors
    const handleUnhandledError = (event: ErrorEvent) => {
      errorLogger.logError(
        ErrorCategory.UNKNOWN,
        ErrorSeverity.HIGH,
        `Unhandled error: ${event.message}`,
        'An unexpected error occurred. Please refresh the page.',
        { filename: event.filename, lineno: event.lineno, colno: event.colno },
        new Error(event.message),
        false
      );
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      errorLogger.logError(
        ErrorCategory.UNKNOWN,
        ErrorSeverity.HIGH,
        `Unhandled promise rejection: ${event.reason}`,
        'An unexpected error occurred. Please refresh the page.',
        { reason: event.reason },
        new Error(event.reason),
        false
      );
    };

    window.addEventListener('error', handleUnhandledError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Cleanup old error logs on app start
    errorLogger.clearOldLogs(7); // Keep logs for 7 days

    // Setup periodic log cleanup
    const logCleanupInterval = setInterval(() => {
      errorLogger.clearOldLogs(7);
    }, 24 * 60 * 60 * 1000); // Daily cleanup

    return () => {
      window.removeEventListener('error', handleUnhandledError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      clearInterval(logCleanupInterval);
      providerEventHandler.cleanup();
    };
  }, []);

  // Show Terms when not on docs/scanner: open if not accepted OR not yet shown this session
  useEffect(() => {
    try {
      const path = location.pathname || '/';
      const isDocs = path.includes('/docs');
      const isScanner = path.includes('/scanner');
      if (!isDocs && !isScanner) {
        const accepted = localStorage.getItem(TERMS_STORAGE_KEY);
        const shownSession = sessionStorage.getItem(TERMS_SESSION_KEY);
        const shouldOpen = accepted !== TERMS_VERSION || !shownSession;
        setIsTermsOpen(shouldOpen);
        if (!shownSession && shouldOpen) {
          sessionStorage.setItem(TERMS_SESSION_KEY, '1');
        }
      } else {
        setIsTermsOpen(false);
      }
    } catch {
      setIsTermsOpen(true);
    }
  }, [location.pathname]);

  const handleOpenWalletModal = () => {
    setIsWalletModalOpen(true);
  };

  const handleCloseWalletModal = () => {
    setIsWalletModalOpen(false);
  };

  const handleAcceptTerms = () => {
    try {
      localStorage.setItem(TERMS_STORAGE_KEY, TERMS_VERSION);
    } catch (e) {
      // ignore storage errors
    }
    setIsTermsOpen(false);
  };

  const handleDeclineTerms = () => {
    setIsTermsOpen(false);
  };

  return (
    <div className="App">
      {/* Unified Header for all pages */}
      <Header onOpenWallet={handleOpenWalletModal} />
      <Routes>
        <Route path="/" element={
          <main>
            <Hero onOpenWallet={handleOpenWalletModal} />
            <StakingTierSection />
            <Features />
            <SwapInterface />
            <EcosystemResponsive />
            <CertikAuditBanner />
            <Vaults />
            <Roadmap />
            <Tokenomics />
          </main>
        } />
        <Route path="/scanner" element={<Scanner />} />
        <Route path="/docs" element={<DocumentationPage />} />
        <Route path="/docs/:section" element={<DocumentationPage />} />
        <Route path="/terms-of-use" element={<TermsOfUse />} />
      </Routes>
      {!isDocsPage && !isScannerPage && <Footer />}
      <TermsModal
        isOpen={isTermsOpen}
        onAccept={handleAcceptTerms}
        onDecline={handleDeclineTerms}
      />
      <WalletModal
        isOpen={isWalletModalOpen}
        onClose={handleCloseWalletModal}
      />
    </div>
  );
}

function App() {
  return (
    <Router>
      <NavigationProvider>
        <AppContent />
      </NavigationProvider>
    </Router>
  );
}

export default App;
